import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Checkbox, FormControlLabel, Button, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import SignatureCanvas from 'react-signature-canvas';

const StyledSignatureBox = styled(Box)(() => ({
    height: 140,
    backgroundColor: '#FAFAFA',
    border: '1px solid #D0D5DD',
    borderRadius: 8,
    mt: 1,
    p: 1,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& canvas': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100% !important',
        height: '100% !important',
    },
}));

type Props = {
    formData: any;
    setFormData: (data: any) => void;
    onValidationChange: (valid: boolean) => void;
    mode?: 'create' | 'edit';
    isEditable?: boolean;
};

const StepDeclarationReview: React.FC<Props> = ({
    formData,
    setFormData,
    onValidationChange,
    mode = 'create',
    isEditable = true,
}) => {
    const sigCanvasRef = useRef<SignatureCanvas | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const canvasContainerRef = useRef<HTMLDivElement | null>(null);

    const [uploadedImageURL, setUploadedImageURL] = useState<string | null>(null);
    const [isSigned, setIsSigned] = useState(formData.signatureDrawn || false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasDrawnSomething, setHasDrawnSomething] = useState(false);
    const [accepted, setAccepted] = useState(formData.declarationAccepted || false);
    const [hasExistingSignature, setHasExistingSignature] = useState(false);

    // Handle signature image restoration and fetched signature files
    useEffect(() => {
        // Handle existing signature image (from upload or draw)
        if (formData.signatureImage && formData.signatureImage instanceof File) {
            if (formData.signatureImage.type.startsWith('image/')) {
                // Create URL for image files
                const reader = new FileReader();
                reader.onloadend = () => {
                    const imageData = reader.result as string;
                    setUploadedImageURL(imageData);
                    setIsSigned(false);
                    setIsDrawing(false);
                    setHasDrawnSomething(false);
                    setHasExistingSignature(false);
                };
                reader.readAsDataURL(formData.signatureImage);
            } else {
                // For non-image files (like PDF), just set the existing signature flag
                setUploadedImageURL(null);
                setIsSigned(false);
                setHasExistingSignature(true);
            }
        } else {
            // No signature image, reset states
            setUploadedImageURL(null);
            setHasExistingSignature(false);
        }
    }, [formData.signatureImage]);

    // Handle canvas resize
    useEffect(() => {
        const resizeCanvas = () => {
            if (canvasContainerRef.current && sigCanvasRef.current) {
                const canvas = sigCanvasRef.current.getCanvas();
                const container = canvasContainerRef.current;
                const ratio = Math.max(window.devicePixelRatio || 1, 1);
                canvas.width = container.offsetWidth * ratio;
                canvas.height = container.offsetHeight * ratio;
                canvas.getContext('2d')?.scale(ratio, ratio);
            }
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        return () => {
            window.removeEventListener('resize', resizeCanvas);
        };
    }, []);

    // Update validation whenever relevant state changes
    useEffect(() => {
        const hasSignature =
            uploadedImageURL !== null ||
            (isSigned && sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) ||
            (formData.signatureImage && formData.signatureImage instanceof File) ||
            hasExistingSignature; // Consider existing signature as valid
        const isValid = accepted && hasSignature;
        onValidationChange(isValid);
    }, [
        accepted,
        uploadedImageURL,
        isSigned,
        formData.signatureImage,
        onValidationChange,
        hasExistingSignature,
    ]);

    const handleDrawBegin = () => {
        setIsDrawing(true);
    };

    const handleDrawEnd = () => {
        setIsDrawing(false);
        if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
            setHasDrawnSomething(true);
        }
    };

    const handleCompleteSignature = () => {
        if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
            setIsSigned(true);
            // Don't clear uploadedImageURL here - let it stay null for drawn signatures
            const signatureData = sigCanvasRef.current.toDataURL();

            // Convert data URL to File object
            const dataURLtoFile = (dataURL: string, filename: string) => {
                const arr = dataURL.split(',');
                const mime = arr[0].match(/:(.*?);/)?.[1];
                const bstr = atob(arr[1]);
                let n = bstr.length;
                const u8arr = new Uint8Array(n);
                while (n--) {
                    u8arr[n] = bstr.charCodeAt(n);
                }
                return new File([u8arr], filename, { type: mime });
            };

            const signatureFile = dataURLtoFile(signatureData, 'signature.png');

            const newFormData = {
                declarationAccepted: accepted,
                signatureDrawn: true,
                signatureImage: signatureFile,
            };

            setFormData(newFormData);
        }
    };

    const handleClearSignature = () => {
        if (sigCanvasRef.current) {
            sigCanvasRef.current.clear();
        }
        setIsSigned(false);
        setIsDrawing(false);
        setHasDrawnSomething(false);
        setUploadedImageURL(null);
        setHasExistingSignature(false);
        setFormData({
            declarationAccepted: accepted,
            signatureDrawn: false,
            signatureImage: null,
        });
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && (file.type.startsWith('image/') || file.type === 'application/pdf')) {
            console.log('File selected:', file.name, file.type);

            if (file.type.startsWith('image/')) {
                // For image files, create a preview
                const reader = new FileReader();
                reader.onloadend = () => {
                    const imageData = reader.result as string;
                    setUploadedImageURL(imageData);
                    if (sigCanvasRef.current) {
                        sigCanvasRef.current.clear();
                    }
                    setIsSigned(false);
                    setIsDrawing(false);
                    setHasDrawnSomething(false);
                    setHasExistingSignature(false);

                    const newFormData = {
                        declarationAccepted: accepted,
                        signatureDrawn: false,
                        signatureImage: file, // Store the actual File object
                    };

                    console.log(
                        'Setting form data with uploaded image:',
                        newFormData.signatureImage?.name,
                    );
                    setFormData(newFormData);
                };
                reader.readAsDataURL(file);
            } else {
                // For PDF files, just store the file without preview
                setUploadedImageURL(null);
                if (sigCanvasRef.current) {
                    sigCanvasRef.current.clear();
                }
                setIsSigned(false);
                setIsDrawing(false);
                setHasDrawnSomething(false);
                setHasExistingSignature(false);

                const newFormData = {
                    declarationAccepted: accepted,
                    signatureDrawn: false,
                    signatureImage: file, // Store the actual File object
                };

                console.log(
                    'Setting form data with uploaded PDF:',
                    newFormData.signatureImage?.name,
                );
                setFormData(newFormData);
            }
        } else {
            console.log('Invalid file type:', file?.type);
        }
        e.target.value = '';
    };

    const handleBrowseClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file && (file.type.startsWith('image/') || file.type === 'application/pdf')) {
            console.log('File dropped:', file.name, file.type);

            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const imageData = reader.result as string;
                    setUploadedImageURL(imageData);
                    if (sigCanvasRef.current) {
                        sigCanvasRef.current.clear();
                    }
                    setIsSigned(false);
                    setIsDrawing(false);
                    setHasDrawnSomething(false);
                    setHasExistingSignature(false);
                    setFormData({
                        ...formData,
                        declarationAccepted: accepted,
                        signatureDrawn: false,
                        signatureImage: file, // Store the actual File object
                    });
                };
                reader.readAsDataURL(file);
            } else {
                setUploadedImageURL(null);
                if (sigCanvasRef.current) {
                    sigCanvasRef.current.clear();
                }
                setIsSigned(false);
                setIsDrawing(false);
                setHasDrawnSomething(false);
                setHasExistingSignature(false);
                setFormData({
                    ...formData,
                    declarationAccepted: accepted,
                    signatureDrawn: false,
                    signatureImage: file, // Store the actual File object
                });
            }
        } else {
            console.log('Invalid file type for drop:', file?.type);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newAccepted = e.target.checked;
        setAccepted(newAccepted);
        setFormData({
            declarationAccepted: newAccepted,
        });
    };

    return (
        <Box>
            <Typography variant='h6' sx={{ fontWeight: 600, color: '#1D1D1D', mb: 2 }}>
                Declaration of Applicant
            </Typography>

            <Box
                sx={{
                    color: '#555',
                    mb: 3,
                    '& ul': {
                        paddingLeft: '18px',
                        margin: 0,
                        listStyle: 'disc',
                    },
                    '& li': {
                        marginBottom: '8px',
                        lineHeight: 1.8,
                        fontSize: '0.875rem',
                    },
                }}
            >
                <ul>
                    <li>I hereby declare that the above-mentioned information is correct.</li>
                    <li>The income specified is that of the total household.</li>
                    <li>
                        Neither I nor my spouse, nor any resident at these premises own any other
                        fixed property.
                    </li>
                    <li>
                        Neither I nor my spouse, nor any resident at these premises are in the
                        service of the state.
                    </li>
                    <li>No business activities on this property.</li>
                    <li>
                        The Municipality may withdraw indigence relief with immediate effect, if it
                        has been established that the indigent relief was granted based on false or
                        fraudulent information.
                    </li>
                    <li>
                        The Municipality may review my application for indigent relief on a regular
                        basis and to visit the property at any reasonable time for the purpose of
                        verifying the information given in the application. Consumption will be
                        limited to prevent further escalation of debt. That I will apply for
                        de-registration if my circumstances improves to such an extent that I no
                        longer meet the requirements for the subsidy.
                    </li>
                    <li>
                        I further give permission that my name be published on the indigent register
                        for public inspection or be furnished to Credit Bureau when is required.
                    </li>
                </ul>
            </Box>

            <FormControlLabel
                control={
                    <Checkbox 
                        checked={accepted} 
                        onChange={handleCheckboxChange} 
                        disabled={!isEditable}
                        sx={{
                            // Override disabled styles to maintain normal appearance
                            '&.Mui-disabled': {
                                color: accepted ? '#2D4677' : '#9E9E9E',
                                opacity: 1,
                            },
                        }}
                    />
                }
                label={
                    <Typography fontWeight={600}>
                        I confirm and accept the indigent declaration terms
                    </Typography>
                }
                sx={{ mb: 4 }}
            />

            <Typography variant='subtitle2' fontWeight={600} mb={1}>
                Declaration Signature
            </Typography>

            <StyledSignatureBox
                ref={canvasContainerRef}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
            >
                {!accepted ? (
                    <Typography sx={{ color: '#999', fontSize: 14 }}>
                        Please accept the declaration to sign or upload.
                    </Typography>
                ) : uploadedImageURL ? (
                    // Show uploaded image preview
                    <Box
                        component='img'
                        src={uploadedImageURL}
                        alt='Uploaded Signature'
                        sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                    />
                ) : formData.signatureImage &&
                  formData.signatureImage.type === 'application/pdf' ? (
                    // Show PDF filename
                    <Typography sx={{ color: '#2D4677', fontSize: 14, textAlign: 'center' }}>
                        PDF file uploaded: {formData.signatureImage.name}
                    </Typography>
                ) : hasExistingSignature && !isSigned && !uploadedImageURL ? (
                    // Show existing signature placeholder
                    <Typography sx={{ color: '#2E7D32', fontSize: 14, textAlign: 'center' }}>
                        ✓ Existing signature on file
                    </Typography>
                ) : (
                    // Show signature canvas (includes drawn signatures)
                    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                        <SignatureCanvas
                            ref={sigCanvasRef}
                            penColor='#1D1D1D'
                            onBegin={handleDrawBegin}
                            onEnd={handleDrawEnd}
                            canvasProps={{
                                className: 'signature-canvas',
                                style: {
                                    width: '100%',
                                    height: '100%',
                                },
                            }}
                            backgroundColor='rgba(0,0,0,0)'
                        />
                        {/* Show user's name and instructions when drawing or has drawn something */}
                        {(isDrawing || hasDrawnSomething) && !isSigned && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 8,
                                    left: 8,
                                    right: 8,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    borderRadius: 1,
                                    p: 1,
                                    fontSize: 12,
                                    color: '#666',
                                }}
                            >
                                <Typography variant="caption" sx={{ color: '#2D4677', fontWeight: 500 }}>
                                    Continue signing...
                                </Typography>
                                {hasDrawnSomething && (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={handleCompleteSignature}
                                        sx={{ 
                                            minWidth: 'auto',
                                            px: 2,
                                            py: 0.5,
                                            fontSize: 11,
                                            backgroundColor: '#2D4677',
                                            '&:hover': {
                                                backgroundColor: '#1a2a4a'
                                            }
                                        }}
                                    >
                                        Complete
                                    </Button>
                                )}
                            </Box>
                        )}
                        {/* Show placeholder text when canvas is empty and not drawing */}
                        {!isDrawing && !hasDrawnSomething && !isSigned && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    color: '#999',
                                    fontSize: 14,
                                    textAlign: 'center',
                                    pointerEvents: 'none',
                                }}
                            >
                                <Typography variant="body2">
                                    Sign here with your mouse or touch
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </StyledSignatureBox>

            {accepted && (isSigned || uploadedImageURL || hasExistingSignature || hasDrawnSomething) && (
                <Stack direction='row' spacing={2} mt={1}>
                    <Button 
                        variant='outlined' 
                        size='small' 
                        onClick={handleClearSignature} 
                        disabled={!isEditable}
                        sx={{
                            // Override disabled styles to maintain normal appearance
                            '&.Mui-disabled': {
                                color: '#132F5F',
                                borderColor: '#132F5F',
                                opacity: 1,
                            },
                        }}
                    >
                        Clear Signature
                    </Button>
                </Stack>
            )}

            <Typography variant='body2' sx={{ mt: 2, mb: 5 }}>
                Drag and drop an image signature or{' '}
                <Box
                    component='span'
                    sx={{
                        color: accepted ? '#F97316' : '#ccc',
                        cursor: accepted ? 'pointer' : 'not-allowed',
                        fontWeight: 500,
                        display: 'inline-block',
                    }}
                    onClick={accepted ? handleBrowseClick : undefined}
                >
                    Browse
                </Box>
            </Typography>

            <input
                type='file'
                accept='image/*,.pdf'
                ref={fileInputRef}
                hidden
                onChange={handleFileSelect}
                disabled={!isEditable}
            />
        </Box>
    );
};

export default StepDeclarationReview;
